import { combineReducers } from 'redux';
import { call, put, takeLatest } from 'redux-saga/effects';
import allocationApi from '../api';
import {
  createApiActionCreators,
  createActionCreator,
  createReducer,
  createActionType,
  REQUEST,
  SUCCESS,
  FAILURE,
} from '../../../common/utils/reduxHelpers';
import message from 'antd/lib/message';
import { fetchAccountAllocationsActions } from '../../account-management/ducks';

/**
 * ACTION TYPES
 */
export const FETCH_ALLOCATIONS = 'allocationManagement/FETCH_ALLOCATIONS';
export const SET_ALLOCATIONS_PAGINATION = 'allocationManagement/SET_ALLOCATIONS_PAGINATION';
export const SET_ALLOCATIONS_FILTER = 'allocationManagement/SET_ALLOCATIONS_FILTER';

export const FETCH_ALLOCATION = 'allocationManagement/FETCH_ALLOCATION';

export const FETCH_LOAN_ALLOCATIONS = 'allocationManagement/FETCH_LOAN_ALLOCATIONS';

export const FETCH_ALLOCATION_INVESTMENTS = 'allocationManagement/FETCH_ALLOCATION_INVESTMENTS';
export const SET_ALLOCATION_INVESTMENTS_PAGINATION =
  'allocationManagement/SET_ALLOCATION_INVESTMENTS_PAGINATION';
export const SET_ALLOCATION_INVESTMENTS_FILTER =
  'allocationManagement/SET_ALLOCATION_INVESTMENTS_FILTER';
export const SELL_ALLOCATIONS = 'allocationManagement/SELL_ALLOCATIONS';

/**
 * ACTIONS
 */
export const fetchAllocationsActions = createApiActionCreators(FETCH_ALLOCATIONS);
export const setAllocationsPaginationAction = createActionCreator(SET_ALLOCATIONS_PAGINATION);
export const setAllocationsFilterAction = createActionCreator(SET_ALLOCATIONS_FILTER);

export const fetchAllocationActions = createApiActionCreators(FETCH_ALLOCATION);

export const fetchLoanAllocationsActions = createApiActionCreators(FETCH_LOAN_ALLOCATIONS)

export const fetchAllocationInvestmentsActions = createApiActionCreators(
  FETCH_ALLOCATION_INVESTMENTS
);
export const setAllocationInvestmentsPaginationAction = createActionCreator(
  SET_ALLOCATION_INVESTMENTS_PAGINATION
);
export const setAllocationInvestmentsFilterAction = createActionCreator(
  SET_ALLOCATION_INVESTMENTS_FILTER
);
export const sellAllocationsActions = createApiActionCreators(SELL_ALLOCATIONS)

/**
 * REDUCERS
 */
const initialState = {
  allocation: null,
  loanAllocations: {},
  allocations: {
    items: [],
    pagination: {
      current: null,
      pageSize: null,
      total: null,
    },
    filter: {
      status: '',
    },
  },
  investments: {
    items: [],
    pagination: {
      current: null,
      pageSize: null,
      total: null,
    },
    filter: {
      status: '',
    },
  },
};

const allocation = createReducer(initialState.allocation, {
  [FETCH_ALLOCATION]: {
    [SUCCESS]: (state, payload) => payload,
    [FAILURE]: () => null,
  },
});

const allocations = createReducer(initialState.allocations, {
  [FETCH_ALLOCATIONS]: {
    [SUCCESS]: (state, payload) => ({
      ...state,
      items: payload.data,
    }),
    [FAILURE]: state => ({
      ...state,
      items: [],
    }),
  },
  [SET_ALLOCATIONS_PAGINATION]: (state, payload) => ({
    ...state,
    pagination: {
      current: payload.pageNumber + 1,
      pageSize: payload.pageSize,
      total: payload.total,
    },
  }),
  [SET_ALLOCATIONS_FILTER]: (state, payload) => ({
    ...state,
    filter: payload,
  }),
  [SELL_ALLOCATIONS]: (state, payload) => ({
    ...state,
  })
});

const loanAllocations = createReducer(initialState.loanAllocations, {
  [FETCH_LOAN_ALLOCATIONS]: {
    [SUCCESS]: (state, payload) => ({
      ...state,
      loanAllocations: payload,
    }),
    [FAILURE]: state => ({
      ...state,
      loanAllocations: [],
    }),
  },
})

const investments = createReducer(initialState.investments, {
  [FETCH_ALLOCATION_INVESTMENTS]: {
    [SUCCESS]: (state, payload) => ({
      ...state,
      items: payload.data,
    }),
    [FAILURE]: state => ({
      ...state,
      items: [],
    }),
  },
  [SET_ALLOCATION_INVESTMENTS_PAGINATION]: (state, payload) => ({
    ...state,
    pagination: {
      current: payload.pageNumber + 1,
      pageSize: payload.pageSize,
      total: payload.total,
    },
  }),
  [SET_ALLOCATION_INVESTMENTS_FILTER]: (state, payload) => ({
    ...state,
    filter: {
      status: payload.status,
    },
  }),
});

export default combineReducers({
  allocation,
  allocations,
  loanAllocations,
  investments,
});

/**
 * SELECTORS
 */
export const selectAllocationState = state => state.allocation;

export const selectAllocation = state => selectAllocationState(state).allocation;

export const selectLoanAllocations = state => selectAllocationState(state).loanAllocations.loanAllocations


export const selectAllocationsState = state => selectAllocationState(state).allocations;
export const selectAllocations = state => selectAllocationsState(state).items;
export const selectAllocationsPagination = state => selectAllocationsState(state).pagination;
export const selectAllocationsFilter = state => selectAllocationsState(state).filter;

export const selectInvestmentsState = state => selectAllocationState(state).investments;
export const selectInvestments = state => selectInvestmentsState(state).items;
export const selectInvestmentsPagination = state => selectInvestmentsState(state).pagination;
export const selectInvestmentsFilter = state => selectInvestmentsState(state).filter;

/**
 * SAGAS
 */
function* fetchAllocation({ payload }) {
  const resp = yield call(allocationApi.fetchAllocation, payload.allocationId);

  if (resp.ok) {
    yield put(fetchAllocationActions.success(resp.data));
  } else {
    yield put(fetchAllocationActions.failure(resp.error));
  }
}

function* fetchAllocations({ payload }) {
  const resp = yield call(allocationApi.fetchAllocations, payload);

  if (resp.ok) {
    yield put(fetchAllocationsActions.success(resp.data));
    yield put(setAllocationsPaginationAction(resp.data));
  } else {
    yield put(fetchAllocationsActions.failure(resp.error));
  }
}

function* fetchLoanAllocations({ payload }) {
  const resp = yield call(allocationApi.fetchLoanAllocations, payload.loanId);

  if (resp.ok) {
    yield put(fetchLoanAllocationsActions.success(resp.data));
  } else {
    yield put(fetchLoanAllocationsActions.failure(resp.error));
  }
}

function* fetchAllocationInvestments({ payload }) {
  const { allocationId, ...options } = payload;
  const resp = yield call(allocationApi.fetchAllocationInvestments, allocationId, options);

  if (resp.ok) {
    yield put(fetchAllocationInvestmentsActions.success(resp.data));
    yield put(setAllocationInvestmentsPaginationAction(resp.data));
  } else {
    yield put(fetchAllocationInvestmentsActions.failure(resp.error));
  }
}

function* sellAllocations({ payload }){
  let accountId = payload.accountId
  const resp = yield call(allocationApi.sellAllocations, payload.body, accountId, payload.buyer, payload.totalAmount, payload.ratio)
  if(resp.ok){
    yield put(sellAllocationsActions.success(resp.data))
    message.success(`${('Investície boli úspešne predané')}.`, 5);
    yield put(fetchAccountAllocationsActions.request({
      accountId }))
  }
  else {
    yield put(sellAllocationsActions.failure(resp.error))
  }
}

export function* allocationSaga() {
  yield takeLatest(createActionType(FETCH_ALLOCATION, REQUEST), fetchAllocation);
  yield takeLatest(createActionType(FETCH_ALLOCATIONS, REQUEST), fetchAllocations);
  yield takeLatest(createActionType(FETCH_LOAN_ALLOCATIONS, REQUEST), fetchLoanAllocations);
  yield takeLatest(SET_ALLOCATIONS_FILTER, fetchAllocations);
  yield takeLatest(
    createActionType(FETCH_ALLOCATION_INVESTMENTS, REQUEST),
    fetchAllocationInvestments
  );
  yield takeLatest(SET_ALLOCATION_INVESTMENTS_FILTER, fetchAllocationInvestments);
  yield takeLatest(createActionType(SELL_ALLOCATIONS, REQUEST), sellAllocations)
}



// WEBPACK FOOTER //
// ./src/features/allocation-management/ducks/index.js